'use client'

import { ImportDataModal } from '@/components/import/ImportDataModal'
import DateRangeFilter from '@/components/date-range-filter'
import ListSortSelect, { AppointmentSort, AppointmentSortOrder } from '@/components/list-sort-select'
import { ClientSelector } from '@/components/client/ClientSelector'
import {
    Consultation,
    getConsultationsByCompany,
    getConsultationStatusColor,
    getConsultationStatusLabel,
} from '@/lib/api/consultations/consultation-api'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { UserSelector } from '@/components/UserSelector'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    FilterField,
    ListEmptyState,
    ListFilterBar,
    ListPageHeader,
} from '@/components/list-page'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdArrowForward,
    MdAutoAwesome,
    MdBusiness,
    MdFileDownload,
    MdHealthAndSafety,
    MdMedicalServices,
    MdMic,
    MdPerson,
    MdPointOfSale,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { BillingStatusFilter } from '@/components/list-page/billing-status-filter'

export default function ConsultationsPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'consultations')
    // Fora da saúde a tela é de "Avaliações".
    const { ClientLabel } = useClientLabel()
    const {
        consultation: consultationLabel,
        Consultation: ConsultationLabel,
        consultations: consultationsLabel,
        Consultations: ConsultationsLabel,
    } = useServiceLabel()
    const [searchInput, setSearchInput] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [filterById, setFilterById] = useState('')
    const [clientFilter, setClientFilter] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    /** Vendedor do atendimento — é o `createdById` (o mesmo que a transferência de vendedor move). */
    const [sellerFilter, setSellerFilter] = useState<number | null>(null)
    /** Status da cobrança vinculada. Vazio = todos; `NONE` = sem cobrança. */
    const [billingStatusFilter, setBillingStatusFilter] = useState('')
    const [dateRange, setDateRange] = useState<{
        startDate?: string
        endDate?: string
    }>({})
    // Criação como padrão: o operador abre a lista para achar o que acabou de
    // registrar, não o que está marcado para a semana que vem.
    const [sortBy, setSortBy] = useState<AppointmentSort>('created')
    // Direção separada do critério: o operador escolhe entre começar pelo mais
    // recente (padrão) ou pelo mais antigo.
    const [sortOrder, setSortOrder] = useState<AppointmentSortOrder>('desc')

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedId = useDebounce(filterById, 300)

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            partnerId: partnerFilter || undefined,
            id: debouncedId ? Number(debouncedId) : undefined,
            clientId: clientFilter || undefined,
            createdById: sellerFilter || undefined,
            billingStatus: billingStatusFilter || undefined,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            sortBy,
            sortOrder,
        }),
        [
            debouncedSearch,
            statusFilter,
            partnerFilter,
            debouncedId,
            clientFilter,
            sellerFilter,
            billingStatusFilter,
            dateRange,
            sortBy,
            sortOrder,
        ]
    )

    const fetchFn = useCallback(
        (params: any) => {
            return getConsultationsByCompany(user!.companyId!, params)
        },
        [user?.companyId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Consultation, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.companyId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const getStatusBadgeClass = (consultation: Consultation) => {
        return (
            getConsultationStatusColor(
                consultation.status,
                consultation.scheduledAt
            ) || 'badge-ghost'
        )
    }

    /** Atalhos de status: as pílulas que ficavam soltas acima dos filtros. */
    const STATUS_TABS: { value: string; label: string }[] = [
        { value: '', label: 'Todas' },
        { value: 'REQUESTED', label: 'Solicitadas' },
        { value: 'SCHEDULED', label: 'Agendadas' },
        { value: 'WAITING', label: 'Aguardando' },
        { value: 'IN_PROGRESS', label: 'Em andamento' },
        { value: 'COMPLETED', label: 'Concluídas' },
        // Consulta cancelada não é excluída: continua listada, e o operador
        // precisa conseguir encontrá-la (é dela que nasce o estorno).
        { value: 'CANCELLED', label: 'Canceladas' },
    ]

    const activeFilters = [
        searchInput,
        filterById,
        clientFilter,
        partnerFilter,
        sellerFilter,
        statusFilter,
        billingStatusFilter,
        dateRange.startDate,
    ].filter(Boolean).length

    const clearFilters = () => {
        setSearchInput('')
        setFilterById('')
        setClientFilter(null)
        setPartnerFilter(null)
        setSellerFilter(null)
        setStatusFilter('')
        setBillingStatusFilter('')
        setDateRange({})
    }

    if (loading && items.length === 0) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando {consultationsLabel}...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <ListPageHeader
                icon={<MdMedicalServices className="text-2xl" />}
                title={ConsultationsLabel}
                subtitle={`${total} ${total === 1 ? consultationLabel : consultationsLabel}`}
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: `Buscar ${consultationsLabel}...`,
                }}
                actions={
                    <>
                        {/* Só ícone: é ação de apoio e o cabeçalho já está
                            cheio. Leva ao fechamento de caixa do próprio
                            operador (consultas + exames + procedimentos). */}
                        <button
                            onClick={() => navigate('/bookings/cash-closing')}
                            className="btn btn-sm btn-ghost btn-circle"
                            title="Fechamento de caixa"
                            aria-label="Fechamento de caixa"
                        >
                            <MdPointOfSale className="text-lg" />
                        </button>
                        {canCreate && (
                            <>
                                <button
                                    className="btn btn-sm btn-outline btn-secondary gap-2"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    <MdFileDownload className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Importar
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/atendimento/scheduling?type=CONSULTATION'
                                        )
                                    }
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Nova {consultationLabel}
                                    </span>
                                </button>
                            </>
                        )}
                    </>
                }
            />

            <ListFilterBar activeCount={activeFilters} onClear={clearFilters}>
                {/* Atalhos de status ocupam a primeira linha da barra: são o
                    filtro mais usado e antes flutuavam soltos, sem nenhuma
                    ligação visual com o resto dos filtros. */}
                <div className="flex w-full flex-wrap gap-1.5">
                    {STATUS_TABS.map(tab => {
                        const active = statusFilter === tab.value
                        return (
                            <button
                                key={tab.value || 'all'}
                                type="button"
                                onClick={() => setStatusFilter(tab.value)}
                                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                                    active
                                        ? 'border-secondary bg-secondary/10 text-secondary'
                                        : 'border-base-300 text-base-content/60 hover:bg-base-200'
                                }`}
                            >
                                {tab.label}
                            </button>
                        )
                    })}
                </div>

                <FilterField label="Número" htmlFor="consultation-id-filter">
                    <input
                        id="consultation-id-filter"
                        type="number"
                        placeholder="0"
                        value={filterById}
                        onChange={e => setFilterById(e.target.value)}
                        className="input input-bordered input-sm w-24"
                        min={1}
                    />
                </FilterField>
                <FilterField label={ClientLabel} className="w-56">
                    <ClientSelector
                        value={clientFilter}
                        onChange={id => setClientFilter(id)}
                        label=""
                        placeholder="Buscar por nome"
                        size="sm"
                    />
                </FilterField>
                {!hidePartnerSelector && (
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={id => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                {!hidePartnerSelector && (
                    <UserSelector
                        value={sellerFilter}
                        onChange={id => setSellerFilter(id)}
                        label="Vendedor"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                <BillingStatusFilter
                    value={billingStatusFilter}
                    onChange={setBillingStatusFilter}
                />
                <DateRangeFilter onChange={setDateRange} />
                <ListSortSelect
                    value={sortBy}
                    onChange={setSortBy}
                    order={sortOrder}
                    onOrderChange={setSortOrder}
                />
            </ListFilterBar>

            {items.length === 0 && !loading ? (
                <ListEmptyState
                    icon={<MdMedicalServices />}
                    title={`Nenhuma ${consultationLabel} encontrada`}
                    description={`Comece agendando sua primeira ${consultationLabel}.`}
                    filtered={activeFilters > 0}
                    onClearFilters={clearFilters}
                    action={
                        canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/atendimento/scheduling?type=CONSULTATION'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Nova {consultationLabel}
                            </button>
                        )
                    }
                />
            ) : (
                <>
                    <div
                        className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${
                            loading ? 'pointer-events-none opacity-50' : ''
                        }`}
                    >
                        {items.map(consultation => {
                            const catalog = consultation.catalog
                            const catalogPrice =
                                catalog?.sellingPrice || catalog?.price || 0
                            const disc = consultation.discount || 0
                            const finalPrice =
                                disc > 0
                                    ? Math.round(
                                          catalogPrice -
                                              (catalogPrice * disc) / 100
                                      )
                                    : catalogPrice

                            return (
                                <div
                                    key={consultation.id}
                                    onClick={() =>
                                        navigate(
                                            `/bookings/consultations/${consultation.id}`
                                        )
                                    }
                                    className="group flex cursor-pointer flex-col rounded-2xl border border-base-300 bg-base-100 transition-colors hover:border-secondary/40 hover:bg-base-200/30"
                                >
                                    <div className="flex flex-col gap-3 p-5">
                                        <div className="flex items-start gap-3">
                                            <h3 className="line-clamp-2 min-w-0 flex-1 text-base font-semibold text-base-content">
                                                {consultation.title ||
                                                    `${ConsultationLabel} #${consultation.id}`}
                                            </h3>
                                            <span
                                                className={`badge badge-sm shrink-0 ${getStatusBadgeClass(consultation)}`}
                                            >
                                                {getConsultationStatusLabel(
                                                    consultation.status,
                                                    consultation.scheduledAt
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            {consultation.client && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdPerson className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {
                                                            consultation.client
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {consultation.professional && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdMedicalServices className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {
                                                            consultation
                                                                .professional
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {consultation.partner && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdBusiness className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {consultation.partner
                                                            .name ||
                                                            consultation.partner
                                                                .tradingName}
                                                    </span>
                                                </div>
                                            )}
                                            {consultation.healthPlan && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdHealthAndSafety className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {
                                                            consultation
                                                                .healthPlan.name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {(consultation.transcriptionStatus ===
                                            'COMPLETED' ||
                                            consultation.aiSummary ||
                                            disc > 0) && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {consultation.transcriptionStatus ===
                                                    'COMPLETED' && (
                                                    <span
                                                        className="badge badge-sm badge-ghost gap-1"
                                                        title="Transcrição disponível"
                                                    >
                                                        <MdMic className="text-xs text-success" />
                                                        Transcrição
                                                    </span>
                                                )}
                                                {consultation.aiSummary && (
                                                    <span
                                                        className="badge badge-sm badge-ghost gap-1"
                                                        title="Resumo por IA disponível"
                                                    >
                                                        <MdAutoAwesome className="text-xs text-secondary" />
                                                        Resumo IA
                                                    </span>
                                                )}
                                                {disc > 0 && (
                                                    <span className="badge badge-sm badge-accent badge-outline">
                                                        {disc}% desc.
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto flex items-center justify-between gap-3 border-t border-base-300 px-5 py-3">
                                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-base-content/50">
                                            <MdAccessTime className="shrink-0 text-sm" />
                                            <span className="truncate">
                                                {format(
                                                    new Date(
                                                        consultation.scheduledAt ||
                                                            consultation.createdAt
                                                    ),
                                                    "dd/MM/yyyy 'às' HH:mm",
                                                    { locale: ptBR }
                                                )}
                                            </span>
                                            {consultation.duration && (
                                                <span className="shrink-0">
                                                    ·{' '}
                                                    {Math.floor(
                                                        consultation.duration /
                                                            60
                                                    )}
                                                    min
                                                </span>
                                            )}
                                        </span>
                                        <span className="flex shrink-0 items-center gap-2">
                                            {catalogPrice > 0 && (
                                                <span className="flex items-baseline gap-1.5">
                                                    {disc > 0 && (
                                                        <span className="text-xs text-base-content/40 line-through">
                                                            {(
                                                                catalogPrice /
                                                                100
                                                            ).toLocaleString(
                                                                'pt-BR',
                                                                {
                                                                    style: 'currency',
                                                                    currency:
                                                                        'BRL',
                                                                }
                                                            )}
                                                        </span>
                                                    )}
                                                    <span className="text-sm font-bold text-secondary">
                                                        {(
                                                            finalPrice / 100
                                                        ).toLocaleString(
                                                            'pt-BR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            }
                                                        )}
                                                    </span>
                                                </span>
                                            )}
                                            <MdArrowForward className="text-base text-base-content/30 transition-colors group-hover:text-secondary" />
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label={consultationLabel}
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.CONSULTATION}
                moduleLabel={ConsultationsLabel}
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getConsultationsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Consultation) => (
                    <div>
                        <p className="truncate text-sm font-medium">
                            {item.title || `${ConsultationLabel} #${item.id}`}
                        </p>
                        <p className="truncate text-xs text-base-content/60">
                            {item.client?.name} - {item.professional?.name}
                        </p>
                        {item.triage && (
                            <span className="badge badge-xs badge-info mt-1">
                                Triagem inclusa
                            </span>
                        )}
                    </div>
                )}
                getItemId={(item: Consultation) => item.id}
                getItemName={(item: Consultation) =>
                    item.title || `${ConsultationLabel} #${item.id}`
                }
                onImportComplete={reload}
            />
        </div>
    )
}
